import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import moment from "moment";
import { postWebViewOutstandingMasterReportDataApiHandler } from "../../../../services/AdminIndex";
import {
  processOutstandingPartyRow,
  calcAmount,
  calcReceivedAmount,
  calcDiscountAmount,
  calcSrPrAmount,
  calcBalanceAmount,
  calcOddDays,
  sumPartyField,
  formatNumber,
  formatDateDisplay,
  isCustomerReport,
} from "../../../../utils/outstandingReportCalc";
import { REPORT_PRINT_CSS, ReportHeader } from "./reportPrintStyle";

// Headless-render-only page for the Auto Report Mail worker: Puppeteer
// navigates here, waits for #report-print-ready, then calls page.pdf().
// Not linked from anywhere in the app UI — this is not the interactive
// Outstanding Report screen (see outstandingReport/OutstandingReport.js),
// but it reuses that page's exact calc/merge logic (outstandingReportCalc.js)
// so the numbers here are never allowed to drift from the manual report.
const dueDateOf = (sale) => {
  if (!sale?.invoiceDate) return "";
  return moment(sale.invoiceDate).add(Number(sale?.creditDays) || 0, "days").format("DD-MM-YYYY");
};

const safeParseJson = (val) => {
  if (val == null) return [];
  if (Array.isArray(val)) return val;
  try {
    const parsed = typeof val === "string" ? JSON.parse(val) : val;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

function BillTable({ reportType, party }) {
  const srPrLabel = isCustomerReport(reportType) ? "SR Amt." : "PR Amt.";
  const numCols = ["Amount", "Received Amt.", "Discount Amt.", srPrLabel, "Balance Amt.", "O/D Days"];
  return (
    <table className="rp-table">
      <thead>
        <tr>
          {["Bill No.", "Bill Date", "Amount", "Received Amt.", "Discount Amt.", srPrLabel, "Balance Amt.", "Due Date", "O/D Days"].map((h) => (
            <th key={h} className={numCols.includes(h) ? "rp-num" : ""}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {party.salesData.map((sale, idx) => (
          <tr key={idx}>
            <td className="rp-nowrap">{sale.invoiceNo}</td>
            <td className="rp-nowrap">{formatDateDisplay(sale.invoiceDate)}</td>
            <td className="rp-num">{formatNumber(calcAmount(sale, reportType))}</td>
            <td className="rp-num">{formatNumber(calcReceivedAmount(sale, reportType))}</td>
            <td className="rp-num">{formatNumber(calcDiscountAmount(sale))}</td>
            <td className="rp-num">{formatNumber(calcSrPrAmount(sale, reportType))}</td>
            <td className="rp-num">{formatNumber(calcBalanceAmount(sale, reportType))}</td>
            <td className="rp-nowrap">{dueDateOf(sale)}</td>
            <td className="rp-num">{calcOddDays(sale)}</td>
          </tr>
        ))}
        <tr className="rp-total">
          <td colSpan={2}>Party Total</td>
          <td className="rp-num">{formatNumber(sumPartyField(party.salesData, calcAmount, reportType))}</td>
          <td className="rp-num">{formatNumber(sumPartyField(party.salesData, calcReceivedAmount, reportType))}</td>
          <td className="rp-num">{formatNumber(sumPartyField(party.salesData, calcDiscountAmount, reportType))}</td>
          <td className="rp-num">{formatNumber(sumPartyField(party.salesData, calcSrPrAmount, reportType))}</td>
          <td className="rp-num">{formatNumber(sumPartyField(party.salesData, calcBalanceAmount, reportType))}</td>
          <td></td>
          <td></td>
        </tr>
      </tbody>
    </table>
  );
}

export default function PrintOutstandingReport() {
  const { companyId, accountId, tillDate, partyType } = useParams();
  const [parties, setParties] = useState([]);
  const [brokerSections, setBrokerSections] = useState([]);
  const [brokerName, setBrokerName] = useState("");
  const [ready, setReady] = useState(false);

  const [searchParams] = useSearchParams();
  const selectedYearStart = searchParams.get("selectedYearStart") || null;
  const company = searchParams.get("company") || "";

  const isBroker = partyType === "broker";
  const reportType = partyType === "supplier" ? "supplier" : "customer";

  useEffect(() => {
    async function loadFlat() {
      const res = await postWebViewOutstandingMasterReportDataApiHandler({
        companyId,
        reportType,
        // Must match the interactive Outstanding Report's "Show" value —
        // getOutstandingReportSP/getOutstandingReport1SP only branch on
        // 'outstanding' | 'date' | 'all'; anything else (e.g. the display
        // label "Bill O/s") matches no branch and silently returns zero rows.
        billType: "outstanding",
        // Without this, the SP's own filter is "customerType IS NULL OR
        // customerType='all' OR (customerType='selected' AND party IN ...)"
        // — omitting it makes the whole clause true unconditionally and
        // ignores `party` entirely, returning every party in the company
        // instead of just this one.
        customerType: "selected",
        party: [accountId],
        tillDate,
        // Every bill-selection subquery in the SP is gated on
        // "(date BETWEEN selectedYearStart/End) OR (date < selectedYearStart
        // AND balance > 0)" — NULL here makes both branches UNKNOWN in SQL,
        // so passing null (as this used to) silently returns zero rows.
        selectedYearStart,
        selectedYearEnd: tillDate,
      });
      const rawRows = res?.data?.[0] || [];
      const processed = rawRows
        .map((r) => processOutstandingPartyRow(r, reportType))
        .filter(Boolean)
        .filter((p) => p.salesData.length > 0);
      setParties(processed);
    }

    async function loadBroker() {
      const fetchSection = (sectionReportType, sectionKey) =>
        postWebViewOutstandingMasterReportDataApiHandler({
          companyId,
          reportType: sectionReportType,
          billType: "outstanding",
          customerType: "all",
          brokerType: "selected",
          broker: accountId,
          tillDate,
          selectedYearStart,
          selectedYearEnd: tillDate,
        }).then((res) => {
          const row = res?.data?.[0]?.[0];
          if (!row) return null;
          const partiesRaw = safeParseJson(row.brokerSalesData);
          // Each broker-nested party's bill array comes back as
          // `salesDetails` (not `salesData` like the flat customer/
          // supplier SPs) — rename before processing so the exact same
          // merge/sort/calc logic applies uniformly.
          const parties = partiesRaw
            .map((p) => processOutstandingPartyRow({ ...p, accountName: p.partyName, salesData: p.salesDetails }, sectionKey))
            .filter(Boolean)
            .filter((p) => p.salesData.length > 0);
          return { brokerName: row.brokerName, parties };
        });

      const [salesSection, supplierSection] = await Promise.all([
        fetchSection("customerbroker", "customer"),
        fetchSection("supplierBroker", "supplier"),
      ]);

      const sections = [];
      if (salesSection?.parties?.length) {
        sections.push({ label: "Customer Outstanding (Broker-wise)", reportType: "customer", parties: salesSection.parties });
      }
      if (supplierSection?.parties?.length) {
        sections.push({ label: "Supplier Outstanding (Broker-wise)", reportType: "supplier", parties: supplierSection.parties });
      }
      setBrokerName(salesSection?.brokerName || supplierSection?.brokerName || "");
      setBrokerSections(sections);
    }

    (isBroker ? loadBroker() : loadFlat()).finally(() => setReady(true));
  }, [companyId, accountId, tillDate, partyType, selectedYearStart]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasData = isBroker ? brokerSections.length > 0 : parties.length > 0;

  const partyName = isBroker ? brokerName : parties[0]?.accountName;
  return (
    <div className="rp-page">
      <style>{REPORT_PRINT_CSS}</style>
      <ReportHeader
        company={company}
        title="Outstanding Report"
        sub={`${partyName ? `${partyName} — ` : ""}As on ${moment(tillDate).format("DD-MM-YYYY")}`}
      />

      {hasData ? (
        isBroker ? (
          brokerSections.map((section) => (
            <div key={section.label}>
              <div className="rp-section">{section.label}</div>
              {section.parties.map((party) => (
                <div key={party.partyId || party.accountName}>
                  <div className="rp-party">{party.accountName}</div>
                  <BillTable reportType={section.reportType} party={party} />
                </div>
              ))}
            </div>
          ))
        ) : (
          parties.map((party) => (
            <div key={party.partyId || party.accountName}>
              <BillTable reportType={reportType} party={party} />
            </div>
          ))
        )
      ) : (
        <div>No outstanding data found for this period.</div>
      )}
      {ready ? <div id="report-print-ready" /> : null}
    </div>
  );
}
